import { Loader2Icon } from 'lucide-react';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { ListPagination } from '@/components/list-pagination';
import {
  FAQ_CATEGORY_SLUG,
  PORTAL_ROOT,
  useAppPaths,
} from '@/products/helpdesk/features/portal/portal-paths';
import { useKbArticles, useKbLookups } from '@/products/helpdesk/features/kb/use-kb';

export function PortalFaqPage() {
  const copy = useVoiceCopy();
  const paths = useAppPaths();
  const lookups = useKbLookups();
  const faqCategory = (lookups.data?.categories ?? []).find(
    (category) => category.slug === FAQ_CATEGORY_SLUG,
  );
  const [page, setPage] = useState(1);
  const list = useKbArticles(
    {
      page,
      pageSize: 25,
      sortBy: 'title',
      sortOrder: 'asc',
      categoryId: faqCategory?.id,
    },
    { enabled: Boolean(faqCategory?.id) },
  );

  return (
    <section className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Häufige Fragen</h1>
        <p className="mt-1 text-sm text-muted-foreground">{copy('helpdesk.portal.faq.lead')}</p>
      </div>

      {(lookups.isPending || list.isPending) && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Die Fragen werden geladen …
        </p>
      )}

      {faqCategory && list.data?.items.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Hier erscheinen bald die häufigsten Fragen. Bis dahin hilft die{' '}
          <Link className="text-primary underline" to={paths.wissen}>
            Hilfe-Übersicht
          </Link>
          .
        </p>
      )}

      <ul className="space-y-3">
        {(list.data?.items ?? []).map((article) => (
          <li key={article.id}>
            <details className="group rounded-md border bg-card p-4">
              <summary className="cursor-pointer list-none font-medium marker:content-none [&::-webkit-details-marker]:hidden">
                <span className="flex items-center justify-between gap-3">
                  {article.title}
                  <span className="text-xs text-muted-foreground group-open:hidden">Öffnen</span>
                  <span className="hidden text-xs text-muted-foreground group-open:inline">
                    Schließen
                  </span>
                </span>
              </summary>
              <div className="mt-3 space-y-3">
                {article.summary && (
                  <p className="text-sm text-muted-foreground">{article.summary}</p>
                )}
                <Button asChild variant="outline" size="sm">
                  <Link to={paths.article(article.slug)}>Ganze Antwort lesen</Link>
                </Button>
              </div>
            </details>
          </li>
        ))}
      </ul>
      {list.data ? (
        <ListPagination
          page={list.data.page}
          pageSize={list.data.pageSize}
          total={list.data.total}
          onPageChange={setPage}
        />
      ) : null}

      <div className="rounded-md border p-4">
        <p className="font-medium">{copy('helpdesk.portal.faq.missing')}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {copy('helpdesk.portal.faq.search-or-ask')}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to={`${PORTAL_ROOT}/wissen`}>Hilfe durchsuchen</Link>
          </Button>
          <Button asChild>
            <Link to={`${PORTAL_ROOT}/tickets/neu`}>Anfrage stellen</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
